

import { Component, Vue, Watch, Prop, Provide, ProvideReactive, Ref } from "vue-property-decorator"
import { namespace, Getter, Mutation, State } from "vuex-class"

import CaseFilters from "@/components/CaseList/CaseFilters.vue"
import NavHeader from "@/components/NavHeader.vue"
import SlideMenu from "@/components/SlideMenu.vue"
import DisplayMessage from "./DisplayMessage.vue"

import { CurrentUser } from "@/types/user"

import { ViewsService } from "@/services/viewsService"
import ZendeskHelp from "./ZendeskHelp.vue"
import ConnectionStatus from "@/types/connectionStatus"
import QuickCreateMenu from "@/components/CaseDetails/QuickCreateMenu.vue"
import { TenantFeatures } from "@/types/tenantFeatures"
import UpdatesViewer from "@/components/Updates/UpdatesViewer.vue"
import { ScheduleMessage } from "@/types/schedule"

const caseStore = namespace("cases")

@Component({
	components: {
		"nav-header": NavHeader,
		"slide-menu": SlideMenu,
		"case-filters": CaseFilters,
		"zendeskHelp": ZendeskHelp,
		"display-message": DisplayMessage,
		"quick-create-menu": QuickCreateMenu,
		"updates-viewer": UpdatesViewer
	}
})
export default class Page extends Vue {
	@Ref()
	slideMenu!: SlideMenu

	@Ref()
	updatesViewer!: UpdatesViewer

	@Provide("currentUser")
	get currentUser (): CurrentUser {
		return this.me
	}

	@Provide("featureEnabled")
	featureEnabled (feature: TenantFeatures) {
		return this.getIsFeatureEnabled(feature)
	}

	@Prop(String)
	title!: string;

	@Prop({ type: Boolean, default: false })
	isDisplayingAdminPortal!: boolean;

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter("getHasIntegrations") hasIntegrations!: boolean

	@Getter("getScreenWidth")
	public screenWidth!: number;

	@caseStore.State viewsService!: ViewsService;

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@Getter getCheckForUpdates!: boolean;

	@caseStore.State
	public caseToast!: string | null

	@caseStore.Mutation
	private setCaseToast!: (message: string | null) => void

	@State
	private userToast!: string | null

	@State public notificationState!: ConnectionStatus | null

	@State
	public scheduleToastMessage!: ScheduleMessage | null

	@Mutation
	private setUserToast!: (message: string | null) => void

	@Mutation
	private setCheckForUpdates!: (checkForUpdates: boolean) => void

	// related to User Updates
	public showUpdatesIfAppropriate: boolean = false
	public displayAllUpdates: boolean = false

	get showError (): boolean {
		return !!this.notificationState && !this.notificationState.connected
	}

	get errorIcon (): string | null {
		if (!this.notificationState || this.notificationState.connected) return null

		if (this.notificationState.ableToReconnect) return "fas fa-spinner fa-spin"

		return "fas fa-wifi-slash"
	}

	@Watch("caseToast")
	onCaseToastChanged (newMessage: string | null) {
		if (newMessage) {
			this.$bvToast.toast(newMessage, {
				title: this.getRecordName,
				variant: "danger",
				solid: true,
				autoHideDelay: 100000
			})

			this.setCaseToast(null)
		}
	}

	@Watch("userToast")
	onUserToastChanged (newMessage: string | null) {
		if (newMessage) {
			this.$bvToast.toast(newMessage, {
				title: "Message",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})

			this.setUserToast(null)
		}
	}

	public async mounted () {
		if (this.screenWidth <= 800) {
			this.viewsService.loadViews()
		}
		this.onCaseToastChanged(this.caseToast)
	}

	@Watch("getCheckForUpdates", { immediate: true })
	public onCheckForUpdatesChanged (newValue: boolean) {
		if (newValue && !this.isDisplayingAdminPortal) {
			this.triggerUpdatesViewer(false)
			this.setCheckForUpdates(false)
		}
	}
	@Watch("scheduleToastMessage")
	scheduleToastMessageChanged (message: ScheduleMessage) {
		if (message && this.getIsFeatureEnabled(TenantFeatures.ScheduleForecastPopup)) {
			this.$bvToast.toast(message.message, {
				title: "Schedule",
				variant: message.variant,
				solid: true
			})
		}
	}

	/** Displays list of routes user can navigate to */
	public get displayRoutes (): any[] {
		return (this.$router as any).options.routes
			.filter((route: any) => {
				return route && route.meta && route.meta.icon
			})
			.map((route: any) => {
				return {
					name: route.name,
					icon: route.meta.icon
				}
			})
	}

	public get currentRouteName () {
		return this.$router.currentRoute.name
	}

	public get activeViewId (): string | null {
		if (this.$router.currentRoute.name === "overview") {
			return this.viewsService.activeViewId
		}
		return null
	}

	public get viewsSummary () {
		if (!this.viewsService.viewSummary) return null
		return this.viewsService.viewSummary.summary
	}

	public gotoPage () {
		this.$router.push({ name: "overview" })
	}

	private openMenu (): void {
		this.slideMenu.open()
	}

	public async changeView (viewId: string | null) {
		this.viewsService.activeViewId = viewId
		if (this.$router.currentRoute.name === "overview") {
			this.viewsService.loadViews()
			this.slideMenu.close()
		} else {
			this.$router.push({ path: "/cases" })
		}
	}

	private refreshConnection () {
		window.location.reload()
	}

	public updatesVisibilityChanged (updatesVisibility: boolean) {
		if (updatesVisibility === false) {
			this.showUpdatesIfAppropriate = false
			this.displayAllUpdates = false
		}
	}

	private triggerUpdatesViewer (displayAllUpdates: boolean = this.displayAllUpdates) {
		this.displayAllUpdates = displayAllUpdates
		if (this.showUpdatesIfAppropriate) {
			// user already has the modal open, trigger a background check
			this.updatesViewer.runBackgroundCheck()
		} else {
			this.showUpdatesIfAppropriate = true
		}
	}

	public get userUpdatesFeatureEnabled (): boolean {
		return this.featureEnabled(TenantFeatures.UserUpdates)
	}
}

